import { useAuth, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { Check, Loader2, MoreVertical, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin-layout";

interface Reservation {
  id: number;
  guest_name: string;
  guest_email: string;
  guest_phone: string | null;
  check_in_date: string;
  check_out_date: string;
  number_of_guests: number;
  special_requests: string | null;
  status: string;
  total_amount: string | null;
  created_at: string;
  villa_name: string;
  villa_number: number;
}

export function AdminReservationsPage() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Protect route - redirect if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate("/login");
    }
  }, [user, isAuthLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchReservations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, searchQuery, user]);

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
      });

      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const response = await authenticatedFetch(`/api/admin/reservations?${params}`);

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (response.status === 403) {
        navigate("/unauthorized");
        return;
      }

      const data = (await response.json()) as {
        success: boolean;
        reservations: Reservation[];
        pagination: { totalPages: number; total: number };
      };

      if (data.success) {
        setReservations(data.reservations);
        setTotalPages(data.pagination.totalPages);
        setTotal(data.pagination.total);
      }
    } catch (error) {
      console.error("Failed to fetch reservations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const response = await authenticatedFetch(`/api/admin/reservations/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (response.status === 403) {
        navigate("/unauthorized");
        return;
      }

      const data = (await response.json()) as { success: boolean };
      if (data.success) {
        toast.success("Reservation status updated");
        fetchReservations();
      }
    } catch (error) {
      console.error("Failed to update reservation:", error);
      toast.error("Failed to update reservation");
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return "px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold";
      case "pending":
        return "px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold";
      case "cancelled":
        return "px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold";
      default:
        return "px-2 py-1 bg-stone-100 text-stone-800 rounded-full text-xs font-semibold";
    }
  };

  // Show loading spinner while checking authentication
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-stone-400" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-['Libre_Baskerville'] text-3xl font-bold text-stone-900">
              Reservations
            </h1>
            <p className="text-stone-600 mt-1">Manage all resort bookings and guest reservations</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-stone-200 rounded-lg mb-6">
          <div className="p-4 flex flex-wrap gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="border border-stone-300 px-3 py-2 rounded text-sm focus:outline-none focus:border-stone-900"
            >
              <option value="all">All Statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-stone-400" />
              <input
                type="text"
                placeholder="Search guest name or email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full border border-stone-300 pl-10 pr-4 py-2 rounded text-sm focus:outline-none focus:border-stone-900"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full size-8 border-b-2 border-stone-900" />
              <p className="mt-4 text-stone-600">Loading reservations...</p>
            </div>
          ) : reservations.length === 0 ? (
            <div className="p-12 text-center text-stone-600">No reservations found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-100 text-left text-xs uppercase tracking-wider text-stone-600">
                    <tr>
                      <th className="p-4">ID</th>
                      <th className="p-4">Guest</th>
                      <th className="p-4">Villa</th>
                      <th className="p-4">Check-in</th>
                      <th className="p-4">Check-out</th>
                      <th className="p-4">Guests</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Total</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {reservations.map((reservation) => (
                      <tr
                        key={reservation.id}
                        className="border-b border-stone-200 hover:bg-stone-50 transition-colors"
                      >
                        <td className="p-4 font-mono text-xs">
                          #{reservation.id.toString().padStart(4, "0")}
                        </td>
                        <td className="p-4">
                          <div className="font-semibold">{reservation.guest_name}</div>
                          <div className="text-xs text-stone-500">{reservation.guest_email}</div>
                        </td>
                        <td className="p-4">{reservation.villa_name}</td>
                        <td className="p-4">{formatDate(reservation.check_in_date)}</td>
                        <td className="p-4">{formatDate(reservation.check_out_date)}</td>
                        <td className="p-4">{reservation.number_of_guests}</td>
                        <td className="p-4">
                          <span className={getStatusBadgeClass(reservation.status)}>
                            {reservation.status}
                          </span>
                        </td>
                        <td className="p-4 font-semibold">
                          {reservation.total_amount
                            ? `$${parseFloat(reservation.total_amount).toFixed(0)}`
                            : "-"}
                        </td>
                        <td className="p-4">
                          <div className="relative">
                            <button
                              onClick={() =>
                                setExpandedId(expandedId === reservation.id ? null : reservation.id)
                              }
                              className="text-stone-600 hover:text-stone-900 transition-colors"
                            >
                              <MoreVertical className="size-5" />
                            </button>

                            {expandedId === reservation.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white border border-stone-200 rounded shadow-lg z-10">
                                <div className="py-1">
                                  {reservation.status !== "confirmed" && (
                                    <button
                                      onClick={() => {
                                        handleStatusChange(reservation.id, "confirmed");
                                        setExpandedId(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2"
                                    >
                                      <Check className="size-4 text-green-600" />
                                      Confirm
                                    </button>
                                  )}
                                  {reservation.status !== "cancelled" && (
                                    <button
                                      onClick={() => {
                                        handleStatusChange(reservation.id, "cancelled");
                                        setExpandedId(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2"
                                    >
                                      <X className="size-4 text-red-600" />
                                      Cancel
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-stone-200 flex justify-between items-center text-sm">
                <div className="text-stone-600">
                  Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, total)} of {total} reservations
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
